import * as React from "react";

import { CssBaseline, createGenerateClassName } from "@material-ui/core";
import { EmployeeDto, getUsersGetLoggedUserInfo } from "Api/Api";
import {
  StylesProvider,
  ThemeProvider,
  createMuiTheme
} from "@material-ui/core/styles";
import styled, {
  DefaultTheme,
  ThemeProvider as StyledThemeProvider
} from "styled-components";

import { Colors } from "Components/Models/Colors";
import { ToastContainer } from "react-toastify";
import { isNullOrEmpty } from "Utils/StringUtils";

type Props = {
  excludeCssBase?: boolean;
};

//https://material-ui.com/styles/advanced/
const theme: DefaultTheme = createMuiTheme(
  {
    spacing: 8,
    shape: {
      borderRadius: 4,
    },
    palette: {
      primary: {
        light: Colors.PrimaryMain,
        main: Colors.PrimaryMain,
        dark: Colors.PrimaryDark,
        contrastText: "#fff",
      },
      secondary: {
        light: Colors.SecondaryLight,
        main: Colors.SecondaryMain,
        dark: Colors.SecondaryMain,
        contrastText: "#000",
      },
      text: {
        primary: Colors.Text,
        secondary: Colors.SecondaryMain,
      },
      error: {
        main: Colors.Red,
      },
      background: {
        default: Colors.Body,
      },
    },
    typography: {
      fontFamily: "'Ubuntu', sans-serif",
      fontSize: 14,
      body1: {
        fontSize: 14,
        fontWeight: 400,
      },
      body2: {
        fontSize: 14,
        fontWeight: 400,
      },
      h1: {
        fontSize: 26,
        fontWeight: 300,
        color: Colors.PrimaryMain,
      },
      h2: {
        fontSize: 22,
        fontWeight: 400,
      },
      h3: {
        fontSize: 18,
        color: Colors.SecondaryMain,
        fontWeight: 300,
      },
      h4: {
        fontSize: 14,
        color: Colors.SecondaryMain,
        fontWeight: 500,
      },
      h5: {
        fontSize: 12,
      },
      button: {
        fontWeight: 500,
      },
    },
  },
  {
    colors: {
      red: Colors.Red,
      yellow: Colors.Yellow,
      body: Colors.Body,
      border: Colors.Border,
      borderDark: Colors.BorderDark,
      blue: Colors.Blue,
      googleBtnBorder: Colors.GoogleBtnBorder,
      white: Colors.White,
    },
    shadow: {
      primary: "0 2px 5px 0 rgba(0, 0, 0, 0.12)",
      primaryHover: "0 2px 5px 0 rgba(0, 0, 0, 0.35)",
    },
    borderRadius: "4px",
    vh: (val: number) => {
      return `calc(var(--vh, 1vh) * ${val})`;
    },
  },
) as DefaultTheme;

const StyledToastContainer = styled(ToastContainer)`
  .Toastify__toast {
    border-radius: ${props => props.theme.borderRadius};
    padding: ${props => props.theme.spacing(2)}px;
  }
  .Toastify__toast--success {
    background-color: ${props => props.theme.palette.success.main};
  }
  .Toastify__toast--error {
    background-color: ${props => props.theme.palette.error.main};
  }
`;

export const Layout: React.FunctionComponent<Props> = props => {
  const generateClassName = createGenerateClassName({
    seed: "ibis",
  });
  const [loggedUserInfo, setData] = React.useState<EmployeeDto>();
  const [isLogged, setIsLogged] = React.useState<boolean>(false);
  const [timer, setTimer] = React.useState<number>();
  const [repeat, setRepeat] = React.useState<number>(0);
  const maxRepeat = 30;

  const isBBugEnabled = () =>
    !isNullOrEmpty(process.env.REACT_APP_BBUG_URL) &&
    !isNullOrEmpty(process.env.REACT_APP_BBUG_ENABLED) &&
    process.env.REACT_APP_BBUG_ENABLED?.toLowerCase() === "true";

  const isInputStyle = () => {
    for (let i = 0; i < document.styleSheets.length; ++i) {
      const styleSheet = document.styleSheets[i];
      const id = (styleSheet.ownerNode as HTMLElement).id;
      if (id === "MuiOutlinedInput-style") {
        return true;
      }
    }
    return false;
  };

  const createStyle = () => {
    if (isInputStyle()) {
      return;
    }
    const styleElm = document.createElement("style");
    styleElm.id = "MuiOutlinedInput-style";
    const text = document.createTextNode(
      ` .ibis-MuiIconButton-root,
        .ibis-MuiCheckbox-root,
        .ibis-MuiOutlinedInput-notchedOutline {
          background-color: transparent !important;
        }

        .ibis-MuiTooltip-popper .ibis-MuiTooltip-tooltip {
          position: static;
          top: auto;
          left: auto;
          right: auto;
          bottom: auto;
          margin: 0 14px;
          padding: 4px 16px;
          overflow: visible;
          border-style: none;
          border-width: 0;
          border-radius: 4px;
          pointer-events: auto;
        }`,
    );
    styleElm.appendChild(text);

    document.head.appendChild(styleElm);
  };

  const checkIsLogged = () => {
    const tokenName = process.env.REACT_APP_IBIS_TOKEN_NAME as string;
    const jwtToken = localStorage.getItem("jwtToken");
    const ibisToken = localStorage.getItem(tokenName);
    if (jwtToken || ibisToken) {
      setIsLogged(true);
    }
  }

  React.useEffect(() => {
    setTimer(setInterval(() => {
      checkIsLogged();
      setRepeat((repeat) => repeat + 1);
    }, 1000));
    return () => {
      clearInterval(timer);
    };
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (isLogged) {
      getUsersGetLoggedUserInfo().then(result => {
        setData(result.json);
      });
    }
  }, [isLogged]);

  React.useEffect(() => {
    const ibisGuidepostWrapper = document.getElementsByClassName("ibis-guidepost-wrapper");
    const isShowLoginBlock = document.getElementById("login-block");
    const isShowIbisGuidepostWrapper = ibisGuidepostWrapper && ibisGuidepostWrapper.length !== 0;

    if (timer && repeat > 0 && (isShowIbisGuidepostWrapper || isShowLoginBlock || repeat >= maxRepeat)) {
      clearInterval(timer);
    }
  }, [timer, repeat]);

  React.useEffect(() => {
    createStyle();
    if (!isBBugEnabled()) {
      return;
    }

    let src = process.env.REACT_APP_BBUG_URL ?? "";
    let existingScript: HTMLScriptElement | undefined = undefined;
    for (let i = 0; i < document.scripts.length; ++i) {
      let script = document.scripts[i];
      if (script.src === src) {
        existingScript = script;
      }
    }

    if (existingScript === undefined) {
      const script = document.createElement("script");
      script.async = true;
      script.defer = true;
      script.src = src;
      script.setAttribute("project", "blogic-ibis-servis");

      setScriptDataAttr(loggedUserInfo, script);

      document.body.appendChild(script);
    } else {
      setScriptDataAttr(loggedUserInfo, existingScript);
    }
    // eslint-disable-next-line
  }, [loggedUserInfo]);

  return (
    <>
      <ThemeProvider theme={theme}>
        {!props.excludeCssBase && <CssBaseline />}
        <StylesProvider generateClassName={generateClassName}>
          <StyledThemeProvider theme={theme}>
            <StyledToastContainer />
            {props.children}
          </StyledThemeProvider>
        </StylesProvider>
      </ThemeProvider>
    </>
  );
};

const setScriptDataAttr = (
  user: { email: string } | undefined | null,
  script: HTMLScriptElement,
) => {
  if (user !== undefined && user !== null) {
    script.setAttribute("data", `{ "user": "${user.email}" }`);
  }
};
